import type { LiveCardMode, PreviewModel } from '@/capture/stampPreview';
import type { TileView } from '@/capture/useStampTile';
import { LocationCard } from './LocationCard';

/**
 * StampPreviewCard (SPEC_STAMP_ROUND_20260924 §C.4) — `<StampPreviewCard model />`: the live stamp preview
 * over the viewfinder, `data-testid="stamp-preview"`, a polite status region (an `<output>`), its a11y text
 * the visible lines. Renders through `LocationCard` (one renderer for the M1 states and the M3 lines) so the
 * fix-less states, the compact clip form and the inner test ids stay exactly what M1 shipped.
 *
 * The model comes from `previewModel(fix, permission, stamp, now, boundFix, watch)` — the caller owns the clock
 * (`useNow`), the in-flight fix, the watch (`useGeoWatch`), the thumbnail (`useStampTile`) and the expanded map
 * (`useStampMosaic` + `StampMapSheet`, opened through `onExpand`); nothing here reads the Geolocation API or the
 * wire, and nothing is fetched.
 */
export function StampPreviewCard({
  model,
  liveCard = 'COORDINATES',
  compact = false,
  tenantName = '',
  tile = null,
  onExpand = null,
  onShowMap = null,
  onHowTo = null,
  onToggle = null,
  now,
}: {
  model: PreviewModel;
  liveCard?: LiveCardMode;
  compact?: boolean;
  /** The "Details" / "Hide details" control between the compact row and the full lines (FSD-20 D7). */
  onToggle?: (() => void) | null;
  tenantName?: string;
  /** The map thumbnail's view (`useStampTile`); null = no thumbnail and nothing to say. */
  tile?: TileView | null;
  /** Opens the expanded map; when absent the thumbnail is not a control. */
  onExpand?: (() => void) | null;
  /** Low-data mode: the "Show map" control that asks for the thumbnail (SPEC_CAPTURE_UX_R2 §7.4). */
  onShowMap?: (() => void) | null;
  /** The denied state's "How to allow" — opens the recovery card (§7.4). */
  onHowTo?: (() => void) | null;
  now?: number;
}): JSX.Element {
  return (
    <LocationCard
      testId="stamp-preview"
      state={model}
      lines={model.kind === 'fix' ? model.lines : null}
      liveCard={liveCard}
      compact={compact}
      tenantName={tenantName}
      tile={tile}
      onExpand={onExpand}
      onShowMap={onShowMap}
      onHowTo={onHowTo}
      onToggle={onToggle}
      {...(now !== undefined ? { now } : {})}
    />
  );
}
